// src/features/settings/components/overenskomster-panel/OverenskomstCreateForm.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Collapse,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { FORM_MAX_WIDTH, FormActions, FormRow, FormSection } from '../../../../components/common/form';

export default function OverenskomstCreateForm({ visible, values, errors, loading, onChange, onSubmit, onCancel }) {
  const { t } = useTranslation(['settings', 'common']);

  const handleDateChange = (field) => (value) => {
    onChange(field, value ? format(value, 'yyyy-MM-dd') : '');
  };

  const numberField = (field) => (
    <TextField
      key={field}
      fullWidth
      label={t(`settings:overenskomster.labels.${field}`)}
      type="number"
      value={values[field]}
      onChange={(e) => onChange(field, e.target.value === '' ? '' : Number(e.target.value))}
      inputProps={{ min: 0, step: field.includes('rate') || field.includes('pct') ? 0.01 : 1 }}
    />
  );

  return (
    <Collapse in={visible}>
      <Box sx={{ maxWidth: FORM_MAX_WIDTH, display: 'flex', flexDirection: 'column', gap: 5, mb: 5 }}>
        <FormSection
          title={t('settings:overenskomster.form.create_title')}
          description={t('settings:overenskomster.form.section_basic')}
        >
          <FormRow widths={[1, 3]} minWidth={140}>
            <TextField
              label={t('settings:overenskomster.labels.code')}
              value={values.code}
              onChange={(e) => onChange('code', e.target.value)}
              required
              fullWidth
              error={Boolean(errors.code)}
              helperText={errors.code}
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label={t('settings:overenskomster.labels.name')}
              value={values.name}
              onChange={(e) => onChange('name', e.target.value)}
              required
              fullWidth
              error={Boolean(errors.name)}
              helperText={errors.name}
              inputProps={{ maxLength: 255 }}
            />
          </FormRow>
          <FormRow>
            <DatePicker
              label={t('settings:overenskomster.labels.valid_from')}
              value={values.valid_from ? new Date(values.valid_from) : null}
              onChange={handleDateChange('valid_from')}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                  error: Boolean(errors.valid_from),
                  helperText: errors.valid_from,
                },
              }}
            />
            <DatePicker
              label={t('settings:overenskomster.labels.valid_to')}
              value={values.valid_to ? new Date(values.valid_to) : null}
              onChange={handleDateChange('valid_to')}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                  error: Boolean(errors.valid_to),
                  helperText: errors.valid_to,
                },
              }}
            />
          </FormRow>
        </FormSection>

        <FormSection title={t('settings:overenskomster.form.section_rates')}>
          <FormRow>
            {numberField('pension_rate_employer')}
            {numberField('pension_rate_employee')}
          </FormRow>
          <FormRow>
            {numberField('fritvalg_pct')}
            {numberField('forskudttid_rate')}
          </FormRow>
          <FormRow>
            {numberField('barsel_foedende_weeks')}
            {numberField('barsel_ikke_foedende_weeks')}
          </FormRow>
          <FormRow>
            {numberField('senior_years_before_pension')}
            {numberField('senior_trigger_age')}
          </FormRow>
          {numberField('education_hours_per_year')}
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(values.assumes_scheduled_break)}
                onChange={(e) => onChange('assumes_scheduled_break', e.target.checked)}
              />
            }
            label={t('settings:overenskomster.labels.assumes_scheduled_break')}
          />
        </FormSection>

        <FormActions
          onCancel={onCancel}
          onSubmit={onSubmit}
          submitLabel={t('common:buttons.create')}
          submitIcon={<SaveIcon />}
          loading={loading}
        />
      </Box>
    </Collapse>
  );
}
